import { IconButton } from '@/components/atoms/Button';
import {
  CancelButton,
  ComparativeContentContainer,
  ComparativeModalButtons,
  ComparativeModalContent,
  ComparativeModalEmptyAmount,
  ComparativeModalHeader,
  ComparativeModalHeaderContent,
  ComparativeModalInput,
  ComparativeModalInputAmount,
  ComparativeModalMoreButton,
  ComparativeModalMoreButtonContainer,
  CompareButton,
} from './styles';
import { Add, Close } from '@mui/icons-material';
import { Text } from '@/components/atoms/text';
import { MunicipalityAutocompleteCompare } from '../autocomplete';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { Divider } from '@mui/material';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { resolveMunicipalityComparisonLimit } from '@/config/municipalityComparisonLimit';

export interface MunicipalityComparativeContent {
  id?: string;
  name?: string;
}

interface ComparativeContentProps {
  municipalityData: MunicipalityComparativeContent;
  open: boolean;
  onClose: () => void;
  onGetMunicipalitiesComparative: (municipalities: MunicipalityComparativeContent[]) => void;
  savedSelections?: MunicipalityComparativeContent[];
  limit?: number;
}

export const ComparativeContent = ({
  municipalityData,
  open,
  onClose,
  onGetMunicipalitiesComparative,
  savedSelections = [],
  limit,
}: ComparativeContentProps) => {
  const theme = useTheme();

  const [inputValues, setInputValues] = useState<Record<number, string>>({});
  const [municipalitiesComparative, setMunicipalitiesComparative] = useState<
    MunicipalityComparativeContent[]
  >([]);

  const { t } = useTranslation();

  const maxMunicipalitiesInComparison =
    limit !== undefined && limit !== null
      ? resolveMunicipalityComparisonLimit(String(limit))
      : resolveMunicipalityComparisonLimit(import.meta.env.VITE_MUNICIPALITY_COMPARISON_LIMIT);

  const shouldShowMoreButton = () =>
    municipalitiesComparative.length < maxMunicipalitiesInComparison;

  useEffect(() => {
    if (savedSelections.length > 0) {
      setMunicipalitiesComparative(savedSelections);
    } else if (municipalityData.id && municipalityData.name) {
      setMunicipalitiesComparative([{ id: municipalityData.id, name: municipalityData.name }]);
    }
  }, [savedSelections, municipalityData.id, municipalityData.name, open]);

  const handleAddMunicipality = (municipality: MunicipalityComparativeContent) => {
    setMunicipalitiesComparative((prev) => [...prev, municipality]);
  };

  const handleInputChange = (index: number, value: string) => {
    setInputValues((prev) => ({ ...prev, [index]: value }));
  };

  const handleRemoveMunicipality = (index: number) => {
    if (index === 0) return;

    const updatedMunicipalities = municipalitiesComparative.filter((_, i) => i !== index);

    if (updatedMunicipalities.length === 0) {
      updatedMunicipalities.push({
        id: municipalityData.id,
        name: municipalityData.name,
      });
    }

    if (updatedMunicipalities.length === 1) {
      onGetMunicipalitiesComparative([]);
    }

    setMunicipalitiesComparative(updatedMunicipalities);
  };

  const handleCancel = () => {
    onClose();
  };

  const canCompare = () => {
    const validMunicipalities = municipalitiesComparative.filter(
      (item) => item.id && item.id.trim() !== ''
    );
    return validMunicipalities.length >= 2;
  };

  const handleCompare = () => {
    const validMunicipalities = municipalitiesComparative.filter(
      (item) => item.id && item.id.trim() !== ''
    );

    if (validMunicipalities.length < 2) {
      setMunicipalitiesComparative([{ id: municipalityData.id, name: municipalityData.name }]);
      onGetMunicipalitiesComparative([]);
      onClose();
      return;
    }

    onGetMunicipalitiesComparative(validMunicipalities);
    onClose();
  };

  return (
    <>
      <ComparativeContentContainer>
        <ComparativeModalHeader>
          <ComparativeModalHeaderContent>
            <Text variant={'h5'}>
              <SafeMarkdown text={t('modal.modal.municipality.compare.title')} />
            </Text>

            <Desktop>
              <IconButton onClick={onClose}>
                <Close sx={{ color: theme.palette.text.primary }} />
              </IconButton>
            </Desktop>
          </ComparativeModalHeaderContent>

          <Text variant="body2" color="text.secondary">
            <SafeMarkdown text={t('modal.modal.municipality.compare.description')} />
          </Text>
        </ComparativeModalHeader>

        <ComparativeModalContent>
          {municipalitiesComparative.map((municipality, index) => (
            <ComparativeModalInput key={`municipality-${municipality.id || index}`}>
              <ComparativeModalInputAmount variant="subtitle1" disabled={!municipality.id}>
                {index + 1}
              </ComparativeModalInputAmount>

              <MunicipalityAutocompleteCompare
                index={index}
                municipality={municipality}
                inputValue={inputValues[index] || ''}
                onInputChange={handleInputChange}
                onChange={(idx, value) => {
                  if (value) {
                    const updatedMunicipalities = [...municipalitiesComparative];
                    updatedMunicipalities[idx] = {
                      id: value.value.toString(),
                      name: value.label,
                    };
                    setMunicipalitiesComparative(updatedMunicipalities);
                    setInputValues((prev) => ({ ...prev, [idx]: '' }));
                  } else {
                    const updatedMunicipalities = [...municipalitiesComparative];
                    updatedMunicipalities[idx] = { id: '', name: '' };
                    setMunicipalitiesComparative(updatedMunicipalities);
                  }
                }}
                municipalitiesComparative={municipalitiesComparative}
                municipalityData={municipalityData}
              />

              {index > 0 && (
                <IconButton onClick={() => handleRemoveMunicipality(index)}>
                  <Close sx={{ color: theme.palette.action.active }} />
                </IconButton>
              )}
            </ComparativeModalInput>
          ))}

          {shouldShowMoreButton() && (
            <ComparativeModalMoreButtonContainer>
              <ComparativeModalEmptyAmount />
              <ComparativeModalMoreButton
                variant="outlined"
                onClick={() => handleAddMunicipality({ id: '', name: '' })}
              >
                <Add color="inherit" /> <SafeMarkdown text={t('button.button.add_municipality')} />
              </ComparativeModalMoreButton>
            </ComparativeModalMoreButtonContainer>
          )}
        </ComparativeModalContent>
      </ComparativeContentContainer>

      <Mobile>
        <Divider variant="fullWidth" />
      </Mobile>

      <ComparativeModalButtons>
        <CompareButton variant="contained" disabled={!canCompare()} onClick={handleCompare}>
          <SafeMarkdown text={t('button.button.compare_municipality_modal')} />
        </CompareButton>

        <CancelButton variant="outlined" onClick={handleCancel}>
          <SafeMarkdown text={t('button.button.cancel')} />
        </CancelButton>
      </ComparativeModalButtons>
    </>
  );
};
